import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, MapPin, Phone, Facebook, Twitter, Instagram, Linkedin, Github } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { contactMessages } from "@/integrations/supabase/client";
import EmailService from "@/services/EmailService";

// Define the form data type
interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  projectDescription: string;
}

const Footer = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useForm<ContactFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      projectDescription: ""
    }
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      // Save contact message to database
      const { error: dbError } = await contactMessages()
        .insert({
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          message: data.projectDescription,
          status: 'unread'
        });

      if (dbError) {
        console.error("Database error:", dbError);
        throw new Error("Failed to save message to database");
      }

      // Send email notification to admin
      try {
        await EmailService.sendEmail({
          to: "info@humblemindai.com",
          subject: "New Contact Form Submission",
          html: `
            <h1>New Contact Message</h1>
            <p><strong>From:</strong> ${data.firstName} ${data.lastName}</p>
            <p><strong>Email:</strong> ${data.email}</p>
            <p><strong>Message:</strong></p>
            <p>${data.projectDescription}</p>
          `
        });
      } catch (emailError) {
        console.error("Email error:", emailError);
        // Don't throw here - we still want to show success if the message was saved
      }

      // Success message
      toast({
        title: "Message sent!",
        description: "We have received your message and will get back to you soon.",
      });

      // Reset form
      form.reset();
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem sending your message. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return <footer id="contact" className="bg-gray-50 pt-20 pb-10">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto mb-16">
          <div className="text-center animate-fade-in">
            <div className="inline-block px-3 py-1 mb-6 rounded-full bg-purple-50 border border-purple-100">
              <span className="text-sm font-medium text-h44s-purple">Got a 🧠 project in mind?</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Let's work together. 🔥</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto text-balance">
              I'm eager to hear about my next big idea or help bring yours to life. Let's chat and figure out if we're the right fit for each other's journey.
            </p>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-2xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input 
                            placeholder="First Name" 
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-h44s-blue" 
                            {...field} 
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input 
                            placeholder="Last Name" 
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-h44s-blue" 
                            {...field} 
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="mb-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input 
                            type="email" 
                            placeholder="Email Address" 
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-h44s-blue" 
                            {...field} 
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="mb-6">
                  <FormField
                    control={form.control}
                    name="projectDescription"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea 
                            placeholder="Project Description" 
                            rows={5} 
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-h44s-blue"
                            {...field}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full px-8 py-6 bg-gradient-to-r from-h44s-blue to-h44s-purple text-white shadow-button button-shine"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Submit Request"}
                </Button>
              </form>
            </Form>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
          <div>
            <h4 className="text-lg font-semibold mb-4">HumbleMind AI</h4>
            <p className="text-gray-600 mb-4">Building innovative solutions for the future of investments.</p>
            <div className="flex space-x-3">
              <a href="#" className="text-gray-500 hover:text-h44s-blue transition-colors">
                <Facebook size={18} />
              </a>
              <a href="#" className="text-gray-500 hover:text-h44s-blue transition-colors">
                <Twitter size={18} />
              </a>
              <a href="#" className="text-gray-500 hover:text-h44s-blue transition-colors">
                <Instagram size={18} />
              </a>
              <a href="#" className="text-gray-500 hover:text-h44s-blue transition-colors">
                <Linkedin size={18} />
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-600 hover:text-h44s-blue transition-colors">Home</Link></li>
              <li><Link to="/opportunities" className="text-gray-600 hover:text-h44s-blue transition-colors">Opportunities</Link></li>
              <li><Link to="/dashboard" className="text-gray-600 hover:text-h44s-blue transition-colors">Dashboard</Link></li>
              <li><Link to="/contact" className="text-gray-600 hover:text-h44s-blue transition-colors">Contact</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-4">Resources</h4>
            <ul className="space-y-2">
              <li><Link to="/schedule-call" className="text-gray-600 hover:text-h44s-blue transition-colors">Schedule a Call</Link></li>
              <li><a href="#" className="text-gray-600 hover:text-h44s-blue transition-colors">Investment Guide</a></li>
              <li><a href="#" className="text-gray-600 hover:text-h44s-blue transition-colors">FAQs</a></li>
              <li><Link to="/login" className="text-gray-600 hover:text-h44s-blue transition-colors">Login</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Mail className="h-5 w-5 text-h44s-purple mr-2 mt-0.5" />
                <span className="text-gray-600">info@humblemindai.com</span>
              </li>
              <li className="flex items-start">
                <Phone className="h-5 w-5 text-h44s-purple mr-2 mt-0.5" />
                <span className="text-gray-600">+27 (0) 73 20 99 100</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500 mb-4 md:mb-0">
              © 2023 HumbleMind AI. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-sm text-gray-500 hover:text-h44s-blue transition-colors">Privacy Policy</a>
              <a href="#" className="text-sm text-gray-500 hover:text-h44s-blue transition-colors">Terms of Service</a>
              <a href="#" className="text-sm text-gray-500 hover:text-h44s-blue transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </div>
    </footer>;
};

export default Footer;
